import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Formik, Form, Field } from "formik";
import "./CreateContractForm.scss";
import {
  InputField,
  AssessmentButton,
  TypeSelect,
  TextEditor,
  AssessmentCheckboxRadio,
  showToast,
} from "ui-components";
import DateRangePicker from "../../../../components/DateRangePicker/DateRangePicker";
import ShiftTime from "../../../Assessment/sections/AssessmentSetting/AdvanceSetting/ShiftTime";
import { setSessionStorageItem } from "../../../../utils/SessionStorageMethod/setSessionStorageItem";
import {
  capitalizeFirstLetter,
  createContractInitialValues,
  scrollToRef,
} from "../../../../utils/Common/Constant";
import {
  createSaveContract,
  getContractDetails,
} from "../../../../service/StaffAugumentation/StaffAugumentationApis";
import { getCurrencyList } from "../../../../service/CvScanning/CvScanning";

interface CreateContractFormProps {
  onClose: () => void;
  candidateId?: string;
  data?: any;
  partnerCompanyId?: string;
  partnerUserId?: string;
  jobcontractId?: string;
  cvListFunData?: any;
  contractReqs?: any;
  contractRef?: any;
}
const durationTypeOptions = [
  { label: "Days", value: "Days" },
  { label: "Weeks", value: "Weeks" },
  { label: "Months", value: "Months" },
  { label: "Years", value: "Years" },
  { label: "Ongoing", value: "Ongoing" },
];
const currencyUnit = [
  { label: "Day", value: "Day" },
  { label: "Hourly", value: "Hourly" },
  { label: "Monthly", value: "Monthly" },
  { label: "Fixed", value: "Fixed" },
];
const billingCycleOptions = [
  { label: "Weekly", value: "Weekly" },
  { label: "Bi-Weekly", value: "Bi-Weekly" },
  { label: "Monthly", value: "Monthly" },
];
const paymentTermsOptions = [
  { label: "net 15", value: "net 15" },
  { label: "net 30", value: "net 30" },
  { label: "net 45", value: "net 45" },
  { label: "net 60", value: "net 60" },
  { label: "net 90", value: "net 90" },
  { label: "Other", value: "other" },
];
const timezoneOptions = [
  { label: "(GMT+05:30) India Standard Time", value: "IST" },
  { label: "(GMT-05:00) Eastern Time (US & Canada)", value: "EST" },
  { label: "(GMT-06:00) Central Time (US & Canada)", value: "CST" },
  { label: "(GMT-07:00) Mountain Time (US & Canada)", value: "MST" },
  { label: "(GMT-08:00) Pacific Time (US & Canada)", value: "PST" },
  { label: "(GMT-09:00) Alaska Time", value: "AKST" },
  { label: "(GMT-10:00) Hawaii-Aleutian Time", value: "HST" },
];
const CreateContractForm: React.FC<CreateContractFormProps> = ({
  onClose,
  candidateId,
  data,
  partnerCompanyId,
  partnerUserId,
  jobcontractId,
  cvListFunData,
  contractReqs,
  contractRef,
}) => {
  const [currencyOptions, setCurrencyOptions] = React.useState([]);
  const { t } = useTranslation();
  const submitAction = useRef("");
  const createSaveContractApi = async (payload: any) => {
    const res = await createSaveContract(payload);
    if (res?.status == 200) {
      showToast(res?.customMessage, "success");
      onClose();
      cvListFunData();
      scrollToRef(contractRef);
    }
  };
  const [contractDetails, setContractDetails] = useState<any>(null);
  useEffect(() => {
    getCurrencyListApi();
  }, []);

  // getContract details api
  const getContractDetailsApi = async () => {
    const res: any = await getContractDetails(jobcontractId);
    if (res?.data) {
      setContractDetails(res?.data);
    }
  };
  useEffect(() => {
    if (jobcontractId) {
      getContractDetailsApi();
    }
  }, [jobcontractId]);
  const getCurrencyListApi = async () => {
    const res: any = await getCurrencyList();
    if (res?.data) {
      const formatted = res?.data.map((item: any) => ({
        value: item.currencyId,
        label: item.code,
      }));
      setCurrencyOptions(formatted);
    }
  };
  return (
    <div className="createContractFormContainer jobModuleStyle">
      <Formik
        initialValues={createContractInitialValues(t)}
        // validationSchema={contractValidationSchema(t)}
        onSubmit={(values) => {
          const payload = {
            jobContractsId: jobcontractId ?? "",
            jobId: data?.jobId ?? contractDetails?.jobs?.jobId,
            partnerCompanyId:
              partnerCompanyId ?? contractDetails?.partnerCompanyId,
            applicantId:
              candidateId ?? contractDetails?.contractResources?.applicantId,
            partnerUserId: partnerUserId ?? contractDetails?.partnerUserId,
            title: values.contractTitle,
            contractType: values.contractType,
            meta: {
              duration: values.duration ? values.duration : "",
              durationType: values.duration ? values.durationType : "",
              amount: values.rate ? values.rate : "",
              currencyId: values.rate ? values.currencyType : "",
              rateUnit: values.rate ? values.currencyUnit : "",
              paymentTerms: values.paymentTerms,
              billingCycle: values.billingCycle,
              shiftStartTime:
                values.contractStartTime + " " + values.contractStartMeridiem,
              shiftEndTime:
                values.contractEndTime + " " + values.contractEndMeridiem,
              timezone: values.timezone,
              noticePeriodDays: values.noticePeriodDays,
              contractTerms: values.contractTerms,
              startDate: values.startDate,
            },
          };
          if (submitAction.current === "create") {
            // check mandatory
            const hasMandatory = contractReqs.some(
              (item: any) => item.isMandatory,
            );
            createSaveContractApi({
              ...payload,
              status: hasMandatory ? "Created" : "Active",
            });
          } else if (submitAction.current === "draft") {
            createSaveContractApi({ ...payload, status: "Draft" });
          }
        }}
      >
        {({ values, errors, touched, setFieldValue, handleSubmit }) => {
          useEffect(() => {
            // edit flow if get jobcontractId
            if (jobcontractId) {
              setFieldValue(
                "contractTitle",
                `${contractDetails?.title ? contractDetails.title : ""}`,
              );
              setFieldValue("contractType", contractDetails?.contractType);
              setFieldValue("duration", contractDetails?.meta?.duration);
              setFieldValue(
                "durationType",
                capitalizeFirstLetter(contractDetails?.meta?.durationType) ||
                  "Months",
              );
              setFieldValue("rate", contractDetails?.meta?.amount);
              setFieldValue("currencyType", contractDetails?.meta?.currencyId);
              setFieldValue(
                "currencyUnit",
                capitalizeFirstLetter(contractDetails?.meta?.rateUnit) ||
                  "Monthly",
              );
              setFieldValue(
                "paymentTerms",
                contractDetails?.meta?.paymentTerms,
              );
              setFieldValue(
                "billingCycle",
                capitalizeFirstLetter(contractDetails?.meta?.billingCycle) ||
                  "Monthly",
              );
              setFieldValue(
                "contractStartTime",
                contractDetails?.meta?.shiftStartTime?.split(" ")[0], // Assuming you want to set the value without the time
              );
              setFieldValue(
                "contractEndTime",
                contractDetails?.meta?.shiftEndTime?.split(" ")[0], // Assuming you want to set the value without the time
              );
              setFieldValue(
                "contractStartMeridiem",
                contractDetails?.meta?.shiftStartTime?.split(" ")[1], // Assuming you want to set the value without the time
              );
              setFieldValue(
                "contractEndMeridiem",
                contractDetails?.meta?.shiftEndTime?.split(" ")[1], // Assuming you want to set the value without the time
              );
              setFieldValue("timezone", contractDetails?.meta?.timezone);
              setFieldValue(
                "noticePeriodDays",
                contractDetails?.meta?.noticePeriodDays,
              );
              setFieldValue(
                "contractTerms",
                contractDetails?.meta?.contractTerms,
              );
              setFieldValue("startDate", contractDetails?.meta?.startDate);
            } else {
              setFieldValue(
                "contractTitle",
                `${data?.jobTitle} ${data?.firstName ? `(${data.firstName})` : ""}`,
              );
            }
          }, [contractDetails]);
          useEffect(() => {
            if (currencyOptions.length > 0) {
              const initialcurrency: any = currencyOptions.find(
                (item: any) => item.label === "INR",
              );
              initialcurrency &&
                setFieldValue("currencyType", initialcurrency?.value);
            }
          }, [currencyOptions]);
          return (
            <Form>
              <div className="d-flex justify-content-between">
                <h2 className="sectionTitle">
                  {t("CONTRACT.CONTRACT_OVERVIEW")}
                </h2>
                <Field name="contractType">
                  {() => (
                    <div className="contractTypeRadioBtn">
                      <div
                        onClick={() =>
                          setFieldValue("contractType", t("CONTRACT.HOURLY"))
                        }
                      >
                        <AssessmentCheckboxRadio
                          type="radio"
                          checked={values.contractType === t("CONTRACT.HOURLY")}
                          label={t("CONTRACT.HOURLY")}
                        />
                      </div>
                      <div
                        onClick={() =>
                          setFieldValue("contractType", t("CONTRACT.FIXED"))
                        }
                      >
                        <AssessmentCheckboxRadio
                          type="radio"
                          checked={values.contractType === t("CONTRACT.FIXED")}
                          label={t("CONTRACT.FIXED")}
                        />
                      </div>
                    </div>
                  )}
                </Field>
              </div>
              <div className="row">
                <div className="inputTitle">
                  <Field name="contractTitle">
                    {() => (
                      <>
                        <label>{t("CONTRACT.TITLE")}</label>
                        <InputField
                          type="text"
                          value={values.contractTitle}
                          onChange={(e: any) =>
                            setFieldValue("contractTitle", e)
                          }
                          placeholder={t("CONTRACT.TITLE_PLACEHOLDER")}
                        />
                      </>
                    )}
                  </Field>
                </div>
              </div>
              <div className="mt-3">
                <h2 className="sectionTitle">
                  {t("CONTRACT.CONTRACT_LENGTH")}
                </h2>
              </div>
              <div className="row">
                <div className="col-md-6 inputTitle">
                  <Field name="duration">
                    {() => (
                      <>
                        <label>{t("CONTRACT.DURATION")}</label>
                        <InputField
                          type="number"
                          value={values.duration}
                          onChange={(e: any) =>
                            setFieldValue("duration", isNaN(e) ? "" : e)
                          }
                          placeholder={t("CONTRACT.DURATION_PLACEHOLDER")}
                          additionalProps={{ min: 0 }}
                        />
                      </>
                    )}
                  </Field>
                  {touched.duration && errors.duration ? (
                    <div className="errorMessage">{errors?.duration}</div>
                  ) : null}
                </div>
                <div className="col-md-6 inputTitle">
                  <Field name="durationType">
                    {() => (
                      <TypeSelect
                        label={t("CONTRACT.DURATION_TYPE")}
                        options={durationTypeOptions}
                        value={values.durationType}
                        onChange={(val: any) =>
                          setFieldValue("durationType", val)
                        }
                        placeholder={t("GENERAL.SELECT")}
                      />
                    )}
                  </Field>
                  {touched.durationType && errors.durationType ? (
                    <div className="errorMessage">{errors?.durationType}</div>
                  ) : null}
                </div>
              </div>
              <div className="mt-3">
                <h2 className="sectionTitle">
                  {t("CONTRACT.COMPENSATION_DETAILS")}
                </h2>
              </div>
              <div className="row commercialDetails">
                <div className="col-md-4 inputTitle">
                  <Field name="rate">
                    {() => (
                      <>
                        <label>{t("CONTRACT.RATE")}</label>
                        <InputField
                          type="number"
                          value={values.rate}
                          onChange={(e: any) =>
                            setFieldValue("rate", isNaN(e) ? "" : e)
                          }
                          placeholder={t("CONTRACT.RATE_PLACEHOLDER")}
                          additionalProps={{ min: 0 }}
                        />
                      </>
                    )}
                  </Field>
                  {touched.rate && errors.rate ? (
                    <div className="errorMessage">{errors?.rate}</div>
                  ) : null}
                </div>
                <div className="col-md-4">
                  <Field name="currencyType">
                    {() => (
                      <TypeSelect
                        label={t("CONTRACT.CURRENCY")}
                        options={currencyOptions}
                        value={values.currencyType}
                        onChange={(val: any) =>
                          setFieldValue("currencyType", val)
                        }
                        placeholder={t("GENERAL.SELECT")}
                      />
                    )}
                  </Field>
                  {touched.currencyType && errors.currencyType ? (
                    <div className="errorMessage">{errors?.currencyType}</div>
                  ) : null}
                </div>
                <div className="col-md-4">
                  <Field name="currencyType">
                    {() => (
                      <TypeSelect
                        label={t("-")}
                        options={currencyUnit}
                        value={values.currencyUnit}
                        onChange={(val: any) =>
                          setFieldValue("currencyUnit", val)
                        }
                        placeholder={t("GENERAL.SELECT")}
                        className="rateUnitLabel"
                      />
                    )}
                  </Field>
                  {touched.currencyUnit && errors.currencyUnit ? (
                    <div className="errorMessage">{errors?.currencyUnit}</div>
                  ) : null}
                </div>
              </div>
              <div className="row displayColumn">
                <div className="col-md-6 inputTitle">
                  <Field name="billingCycle">
                    {() => (
                      <TypeSelect
                        label={t("CONTRACT.BILLING_CYCLE")}
                        options={billingCycleOptions}
                        value={values.billingCycle}
                        onChange={(val: any) =>
                          setFieldValue("billingCycle", val)
                        }
                        placeholder={t("CONTRACT.BILLING_CYCLE_PLACEHOLDER")}
                      />
                    )}
                  </Field>
                </div>
                <div className="col-md-6 inputTitle">
                  <Field name="paymentTerms">
                    {() => (
                      <TypeSelect
                        label={t("CONTRACT.PAYMENT_TERMS")}
                        options={paymentTermsOptions}
                        value={values.paymentTerms}
                        onChange={(val: any) =>
                          setFieldValue("paymentTerms", val)
                        }
                        placeholder={t("CONTRACT.PAYMENT_TERMS_PLACEHOLDER")}
                      />
                    )}
                  </Field>
                  {touched.paymentTerms && errors.paymentTerms ? (
                    <div className="errorMessage">{errors?.paymentTerms}</div>
                  ) : null}
                </div>
              </div>
              <div className="mt-3">
                <h2 className="sectionTitle">{t("CONTRACT.TIMING_DETAILS")}</h2>
              </div>
              <div className="inputTitle">
                <Field name="timezone">
                  {() => (
                    <TypeSelect
                      label={t("CONTRACT.TIMEZONE")}
                      options={timezoneOptions}
                      value={values.timezone}
                      onChange={(val: any) => setFieldValue("timezone", val)}
                      placeholder={t("GENERAL.SELECT")}
                    />
                  )}
                </Field>
              </div>
              <div className="inputTitle">
                <Field name="contractStartTime">
                  {() => (
                    <ShiftTime
                      startMeridiem={values.contractStartMeridiem}
                      endMeridiem={values.contractEndMeridiem}
                      onStartTimeChange={(e: any) => {
                        setFieldValue("contractStartTime", e);
                        setSessionStorageItem("contractStartTime", e);
                      }}
                      onEndTimeChange={(e: any) => {
                        setFieldValue("contractEndTime", e);
                        setSessionStorageItem("contractEndTime", e);
                      }}
                      onStartMeridiemChange={(e: any) => {
                        setFieldValue("contractStartMeridiem", e);
                        setSessionStorageItem("contractStartMeridiem", e);
                      }}
                      onEndMeridiemChange={(e: any) => {
                        setFieldValue("contractEndMeridiem", e);
                        setSessionStorageItem("contractEndMeridiem", e);
                      }}
                      startTime={values.contractStartTime}
                      endTime={values.contractEndTime}
                    />
                  )}
                </Field>
              </div>
              <div className="mt-5">
                <h2 className="sectionTitle">{t("CONTRACT.OTHER_DETAILS")}</h2>
              </div>
              <div className="row displayColumn">
                <div className="col-md-6 inputTitle">
                  <Field name="startDate">
                    {() => (
                      <>
                        <label>{t("CONTRACT.START_DATE")}</label>
                        <div>
                          <DateRangePicker
                            label=""
                            selectedDate={values.startDate}
                            onDateChange={(date: string) =>
                              setFieldValue("startDate", date)
                            }
                          />
                        </div>
                      </>
                    )}
                  </Field>
                  {touched.startDate && errors.startDate ? (
                    <div className="errorMessage">{errors?.startDate}</div>
                  ) : null}
                </div>
                <div className="col-md-6 inputTitle">
                  <Field name="noticePeriodDays">
                    {() => (
                      <>
                        <label>{t("CONTRACT.NOTICE_PERIOD")}</label>
                        <InputField
                          type="number"
                          value={values.noticePeriodDays}
                          onChange={(e: any) =>
                            setFieldValue("noticePeriodDays", isNaN(e) ? "" : e)
                          }
                          placeholder={t("CONTRACT.NOTICE_PERIOD_PLACEHOLDER")}
                          additionalProps={{ min: 0 }}
                        />
                      </>
                    )}
                  </Field>
                  {touched.noticePeriodDays && errors.noticePeriodDays ? (
                    <div className="errorMessage">
                      {errors?.noticePeriodDays}
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="inputTitle">
                <Field name="contractTerms">
                  {() => (
                    <>
                      <label>{t("CONTRACT.CONTRACT_TERMS")}</label>
                      <TextEditor
                        value={values.contractTerms}
                        onChange={(val: any) =>
                          setFieldValue("contractTerms", val)
                        }
                        placeholder={t("CONTRACT.CONTRACT_TERMS_PLACEHOLDER")}
                      />
                    </>
                  )}
                </Field>
                {touched.contractTerms && errors.contractTerms ? (
                  <div className="errorMessage">{errors?.contractTerms}</div>
                ) : null}
              </div>
              <div className="formActions">
                <AssessmentButton
                  className="submitBtn"
                  type="button"
                  onClick={() => {
                    submitAction.current = "draft";
                    handleSubmit();
                  }}
                >
                  {t("GENERAL.DRAFT")}
                </AssessmentButton>
                <AssessmentButton
                  type="button"
                  onClick={() => {
                    submitAction.current = "create";
                    handleSubmit();
                  }}
                  className="submitBtn"
                >
                  {t("GENERAL.CREATE")}
                </AssessmentButton>
              </div>
              {/* DocumentList */}
              <div className="contractDocListSec">
                <div className="mt-3 mb-3">
                  <h4 className="sectionTitle">{t("CONTRACT.DOCUMENTS")}</h4>
                </div>
                {contractReqs.map((item: any, ind: number) => (
                  <div
                    className="contractDocList d-flex justify-content-between align-items-center mb-3 pb-3"
                    key={ind}
                  >
                    <h6>
                      {item.title}
                      {item.isMandatory === true ? <span>*</span> : ""}
                    </h6>
                  </div>
                ))}
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default CreateContractForm;
